import SectionHeader from "@/components/SectionHeader";
import { CheckCircle2, Award, Users, Briefcase, HeartHandshake } from "lucide-react";

const BIO_IMG = "https://customer-assets.emergentagent.com/job_linda-burke/artifacts/l1o4htin_IMG_7945.jpeg";

const quickFacts = [
  "Mayor of South Lebanon (2024–Present)",
  "South Lebanon Council Member (2019–2023)",
  "President, Warren County Community Services Board of Trustees (2023–2025)",
  "Republican Women of Warren County, President (2010–2015)",
  "Warren County Republican Central Committee, Chair (2016–2018)",
  "Warren County United Way Board (2019–2022)",
];

const elected = [
  { years: "2024 – Present", role: "Mayor", org: "City of South Lebanon" },
  { years: "2019 – 2023", role: "Council Member", org: "City of South Lebanon" },
];

const involvement = [
  { years: "2019 – Present", role: "100 Who Care in Warren County" },
  { years: "2023 – 2025", role: "President, Warren County Community Services Board of Trustees" },
  { years: "2021 – 2022", role: "Vice President, Warren County Community Services Board of Trustees" },
  { years: "2019 – 2025", role: "Warren County Community Services Board of Trustees" },
  { years: "2019 – 2022", role: "Warren County United Way — Board & Campaign Committee Chair" },
  { years: "2016 – 2018", role: "Chair, Warren County Republican Central Committee" },
  { years: "2014 – Present", role: "Warren County Republican Central Committee, Precinct Member" },
  { years: "2012 – Present", role: "Warren County Republican Party" },
  { years: "2010 – 2015", role: "President, Republican Women of Warren County" },
  { years: "2014", role: "1st Vice President, Ohio Federation of Republican Women (OFRW)" },
  { years: "2010 – 2012", role: "OFRW Southwest Ohio District Vice President" },
  { years: "2009 – 2010", role: "OFRW Caring for America Chair" },
  { years: "1998 – 2014", role: "Ohio Federation of Republican Women (OFRW)" },
];

export default function MeetLinda() {
  return (
    <div className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* HERO */}
        <div className="grid lg:grid-cols-12 gap-12 items-start mb-20">
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 border-2 border-accent" aria-hidden />
              <img
                src={BIO_IMG}
                alt="Mayor Linda Burke"
                className="relative w-full h-auto object-cover aspect-square"
                data-testid="meet-linda-photo"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <div className="flag-rule"><span /><span /><span /></div>
              <span className="text-xs uppercase tracking-[0.22em] font-bold text-accent">
                Meet Mayor Linda Burke
              </span>
            </div>
            <h1 className="font-serif text-5xl md:text-6xl font-black text-primary leading-tight tracking-tight">
              A neighbor first. <br />
              A leader for South Lebanon.
            </h1>
            <div className="mt-8 space-y-5 text-lg text-foreground/85 leading-relaxed">
              <p>
                Linda Burke has dedicated years of service to the South Lebanon and Warren County communities as a volunteer, advocate, and leader. She served on South Lebanon City Council from 2019 to 2023, and after earning the trust of voters was elected Mayor in November 2023 — officially taking office on January 1, 2024.
              </p>
              <p>
                Since becoming mayor, Linda has focused on responsible growth, fiscal stewardship, public safety, infrastructure improvements, and preserving the strong sense of community that makes South Lebanon a special place to call home.
              </p>
              <p>
                Prior to serving as Mayor, Linda built a reputation as a respected community leader through her involvement in local organizations, including serving as President of the Warren County Community Services Board of Trustees.
              </p>
              <p>
                Linda believes local government works best when it remains transparent, accessible, and focused on the needs of residents. As South Lebanon continues to grow, she is committed to ensuring that growth is managed responsibly while protecting the values and character that have made the community successful.
              </p>
            </div>
          </div>
        </div>

        {/* QUICK FACTS */}
        <div className="bg-card border border-border p-8 md:p-12 mb-20" data-testid="quick-facts">
          <div className="flex items-center gap-3 mb-6">
            <div className="flag-rule"><span /><span /><span /></div>
            <span className="text-xs uppercase tracking-[0.22em] font-bold text-accent">Quick Facts</span>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-8 leading-tight">
            What you should know about Linda.
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
            {quickFacts.map((q) => (
              <li key={q} className="flex gap-3 items-start text-foreground/85">
                <CheckCircle2 size={20} className="shrink-0 mt-0.5 text-accent" />
                <span className="leading-relaxed">{q}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* WHY RUNNING */}
        <div className="grid lg:grid-cols-12 gap-10 items-start mb-20">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="flag-rule"><span /><span /><span /></div>
              <span className="text-xs uppercase tracking-[0.22em] font-bold text-accent">In Her Own Words</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-primary leading-tight tracking-tight">
              Why I'm running for re-election.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <blockquote className="border-l-4 border-accent pl-6 text-xl text-foreground/90 italic leading-relaxed">
              "Since taking office in 2024, we have made important progress, but there is still work to do. South Lebanon continues to grow at a rapid pace, bringing both opportunities and challenges. I am running for re-election to continue providing steady leadership, thoughtful planning, and a commitment to keeping South Lebanon a safe, vibrant, and welcoming community for generations to come."
            </blockquote>
            <div className="mt-5 text-sm uppercase tracking-[0.22em] text-muted-foreground font-bold">
              — Mayor Linda Burke
            </div>
          </div>
        </div>

        {/* ELECTED SERVICE */}
        <SectionHeader
          eyebrow="Elected Service"
          title="Years of leadership for South Lebanon."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20">
          {elected.map((e) => (
            <div key={e.years} className="bg-card border border-border p-8 relative" data-testid={`elected-${e.role.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="absolute top-0 left-0 right-0 h-1 bg-accent" aria-hidden />
              <div className="flex items-start gap-5">
                <div className="shrink-0 w-14 h-14 bg-primary text-primary-foreground flex items-center justify-center">
                  <Award size={24} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] font-bold text-accent">{e.years}</div>
                  <h3 className="font-serif text-2xl font-bold text-primary mt-1 leading-tight">{e.role}</h3>
                  <p className="text-muted-foreground mt-1">{e.org}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* COMMUNITY INVOLVEMENT TIMELINE */}
        <SectionHeader
          eyebrow="Community Involvement"
          title="A lifetime of showing up."
          intro="Long before public office, Linda has served Warren County through dozens of community, charitable, and civic organizations."
        />
        <div className="bg-card border border-border" data-testid="involvement-timeline">
          <ul>
            {involvement.map((item, i) => (
              <li
                key={item.role + item.years}
                className={`flex flex-col md:flex-row gap-2 md:gap-8 md:items-center p-5 md:px-8 ${i !== involvement.length - 1 ? "border-b border-border" : ""}`}
              >
                <div className="md:w-44 shrink-0 text-xs md:text-sm uppercase tracking-[0.18em] font-bold text-accent">
                  {item.years}
                </div>
                <div className="flex-1 text-foreground/85 leading-relaxed flex items-start gap-3">
                  <HeartHandshake size={18} className="text-primary shrink-0 mt-1 hidden md:block" />
                  <span>{item.role}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
